import type { TransportItem } from "@grafana/faro-web-sdk";
import { redactUrl, scrub } from "./scrub";

export type ScrubRule = readonly [RegExp, string];

export interface SanitizerOptions {
  /** Maps a page pathname to a route template; defaults to `redactUrl`. */
  routeTemplate?: (pathname: string) => string;
  /** App-specific secrets to mask in free text, applied after the shared rules. */
  extraScrub?: readonly ScrubRule[];
}

const URL_LIKE = /^(?:[a-z][a-z0-9+.-]*:\/\/|\/)/i;
const DROPPED_SPAN_ATTRIBUTES = new Set(["http.user_agent", "user_agent.original"]);
// Faro's own numeric timings would otherwise be mistaken for card numbers.
const USER_ACTION_EVENT = "faro.user.action";
const USER_ACTION_TIMINGS = new Set(["userActionStartTime", "userActionEndTime", "userActionDuration"]);
const NUMERIC = /^\d+(?:\.\d+)?$/;

interface OtlpAttribute { key: string; value?: { stringValue?: string } }
interface OtlpPayload { resourceSpans?: { scopeSpans?: { spans?: { attributes?: OtlpAttribute[] }[] }[] }[] }

const isUserActionTiming = (key: string, value: string) => USER_ACTION_TIMINGS.has(key) && NUMERIC.test(value);

/** Last line of defence before anything leaves the browser: no query strings, IDs in URLs, PII in text, or user agent. */
export function createSanitizer({ routeTemplate, extraScrub = [] }: SanitizerOptions = {}) {
  const scrubText = (value: string) =>
    extraScrub.reduce((text, [pattern, replacement]) => text.replace(pattern, replacement), scrub(value));
  const redact = (value: string) => scrubText(redactUrl(value));
  const clean = (value: string) => (URL_LIKE.test(value) ? redact(value) : scrubText(value));

  const pageUrl = (url: string) => {
    if (!routeTemplate) return redact(url);
    try {
      const parsed = new URL(url);
      return parsed.origin + scrubText(routeTemplate(parsed.pathname));
    } catch {
      return redact(url);
    }
  };

  function cleanRecord<T extends Record<string, unknown> | undefined>(record: T, keep?: (key: string, value: string) => boolean): T {
    if (!record) return record;
    return Object.fromEntries(
      Object.entries(record).map(([key, value]) => [
        key, typeof value === "string" && !keep?.(key, value) ? clean(value) : value,
      ]),
    ) as T;
  }

  function cleanSpans(payload: OtlpPayload) {
    for (const resource of payload.resourceSpans ?? []) {
      for (const scope of resource.scopeSpans ?? []) {
        for (const span of scope.spans ?? []) {
          span.attributes = span.attributes
            ?.filter(attribute => !DROPPED_SPAN_ATTRIBUTES.has(attribute.key))
            .map(attribute => typeof attribute.value?.stringValue === "string"
              ? { ...attribute, value: { ...attribute.value, stringValue: clean(attribute.value.stringValue) } }
              : attribute);
        }
      }
    }
  }

  return function sanitizeTransportItem(item: TransportItem): TransportItem {
    const { page, browser } = item.meta;
    if (page?.url) item.meta = { ...item.meta, page: { ...page, url: pageUrl(page.url) } };
    if (browser?.userAgent) item.meta = { ...item.meta, browser: { ...browser, userAgent: undefined } };

    const payload = item.payload as Record<string, unknown>;
    switch (item.type) {
      case "exception":
        if (typeof payload["value"] === "string") payload["value"] = scrubText(payload["value"]);
        payload["context"] = cleanRecord(payload["context"] as Record<string, unknown> | undefined);
        break;
      case "log":
        if (typeof payload["message"] === "string") payload["message"] = scrubText(payload["message"]);
        payload["context"] = cleanRecord(payload["context"] as Record<string, unknown> | undefined);
        break;
      case "event":
        payload["attributes"] = cleanRecord(
          payload["attributes"] as Record<string, unknown> | undefined,
          payload["name"] === USER_ACTION_EVENT ? isUserActionTiming : undefined,
        );
        break;
      case "measurement":
        payload["context"] = cleanRecord(payload["context"] as Record<string, unknown> | undefined);
        break;
      case "trace":
        cleanSpans(payload as OtlpPayload);
        break;
    }
    return item;
  };
}
